import gql from 'graphql-tag';
import className from 'classnames/bind';
import EventsListEvent from './Event';
import { useInView, motion } from "motion/react";
import { useRef } from "react";
import { getHighlightMarkup, getLinkData } from '@/core/utils';
import { useViewSize } from '@/providers/ViewSizeContext';
import { Button, Container, HeadlineAnim, RevealWrapper } from '@components';
import { usePageWrapperData } from '@/providers/PageWrapperContext';
import { EventFragment } from '@/fragments/Event';
import { MouseProvider } from '@/providers/MouseContext';
import { ListingEventsFragment, Event } from '@graphqlTypes';

import { AnimVals } from "@/utils/animUtils";

import styles from './ListingEvents.module.scss';
const cx = className.bind(styles);

export default function ListingEvents(props: ListingEventsFragment) {
  const { type, headline, events, cta } = props;
  const [listType] = type;
  const headlineMarkup = getHighlightMarkup(headline);
  const { upcomingEvents } = usePageWrapperData(); 
  const isUpcoming = listType === 'recent';
  const eventsData = (isUpcoming ? upcomingEvents : events.nodes).filter((event): event is Event =>
    event != null && event.__typename === 'Event'
  ) as Event[];
  const ctaData = getLinkData(cta);

  // -------------------- Animate in

  const { isDesktop } = useViewSize();

  const isRevealEnabled = true;

  const rootEl = useRef<HTMLDivElement>(null);
  const REVEAL = AnimVals;
  const triggerPercent = isDesktop ? REVEAL.lg.triggerPercent : REVEAL.sm.triggerPercent;
  const isInView = useInView(rootEl, { amount: triggerPercent, once: REVEAL.once });

  // -------------------- Render

  if (!eventsData.length) return null;

  return (
    <section className={cx(['listing-events', `listing-events-${listType}`])} ref={rootEl}>
      <Container className={cx("list-container")}>
      <RevealWrapper isInView={isInView}>
        <HeadlineAnim
          tag="h2"
          text={headlineMarkup}
          className={cx("headline", "title-uc-l")}
          isRevealEnabled={isRevealEnabled}
          isInView={isInView}
        />
        <ul className={cx("events")}>
          <MouseProvider>
            {eventsData.map((event, index) => (
              <EventsListEvent
                event={event}
                index={index}
                isRevealEnabled={isRevealEnabled}
                isInView={isInView}
                key={event.databaseId}
              />
            ))}
          </MouseProvider>
        </ul>
        <motion.div
          className={cx("button-row")}
          initial={isRevealEnabled ? { opacity: AnimVals.opacity, y: AnimVals.sm.distance } : {}}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{
            delay: 0.4 + (eventsData.length * AnimVals.sm.delay),
            duration: AnimVals.sm.duration,
            ease: AnimVals.ease
          }}
        >
          { ctaData && <Button className={cx("cta")} {...ctaData} /> }
        </motion.div>
      </RevealWrapper>
      </Container>
    </section>
  )
}

ListingEvents.fragments = {
  entry: gql`
    ${EventFragment}
    fragment ListingEvents on FlexiblePageContentContentEventsLayout {
      type
      headline
      cta {
        linkType
        label
        externalLink
        page {
          node {
            uri
          }
        }
      }
      events {
        nodes {
          ... on Event {
            ...EventFragment
          }
        }
      }
    }
  `,
};
